import { categoryLabels } from "@/lib/categoryLabels";
import {
  categoryStructure,
  type CategoryStructure,
  type SubCategory,
} from "@/lib/categoryStructure";
import {
  excludePickerHiddenSubcategories,
  getActiveSubcategories,
  isHiddenFromCatalogPickerSubcategory,
} from "@/lib/activeCategories";

const CATEGORY_VALUES = Object.keys(categoryLabels) as readonly string[];
const ALLOWED = new Set(CATEGORY_VALUES);

export type NearMeSubcategoryOption = {
  id: string;
  name: string;
  parentCategory: string;
  parentLabel: string;
};

export type NearMePickerOptions = {
  categories: readonly string[];
  subcategories: readonly NearMeSubcategoryOption[];
};

/**
 * Build near-me picker options from a category structure
 * (typically platform-active from `useActiveCategoryStructure`).
 */
export function buildNearMePickerOptionsFromStructure(
  structure: CategoryStructure[],
): NearMePickerOptions {
  const allowedCategorySet = new Set(
    structure.map((c) => c.id).filter((id) => ALLOWED.has(id)),
  );
  const subcats = getActiveSubcategories(structure).filter((s) =>
    allowedCategorySet.has(s.parentCategory),
  );
  const categories = [...allowedCategorySet].sort((a, b) => a.localeCompare(b));
  const subcategories: NearMeSubcategoryOption[] = subcats
    .map((s) => ({
      id: s.id,
      name: s.name,
      parentCategory: s.parentCategory,
      parentLabel:
        (categoryLabels as Record<string, string>)[s.parentCategory] ??
        s.parentCategory,
    }))
    .sort((a, b) => {
      const parentCmp = a.parentLabel.localeCompare(b.parentLabel);
      if (parentCmp !== 0) return parentCmp;
      return a.name.localeCompare(b.name);
    });
  return { categories, subcategories };
}

/** Sorted category values for near-me filter UI (domain labels, not DB enum). */
export const NEAR_ME_CATEGORY_OPTIONS: readonly string[] = [
  ...CATEGORY_VALUES,
].sort((a, b) => a.localeCompare(b));

const ALLOWED_CATEGORY_SET = new Set(NEAR_ME_CATEGORY_OPTIONS);

/** Picker-visible subcats only (legacy static fallback; UI prefers activation). */
const SUBCATS_FOR_ALLOWED_CATEGORIES: readonly SubCategory[] =
  getActiveSubcategories(
    excludePickerHiddenSubcategories(categoryStructure),
  ).filter((s) => ALLOWED_CATEGORY_SET.has(s.parentCategory));

const SUBCATEGORY_IDS = new Set(
  SUBCATS_FOR_ALLOWED_CATEGORIES.map((s) => s.id),
);

export const NEAR_ME_SUBCATEGORY_OPTIONS: readonly NearMeSubcategoryOption[] =
  buildNearMePickerOptionsFromStructure(
    excludePickerHiddenSubcategories(categoryStructure),
  ).subcategories;

/** For V2.2 usability assist: quick examples shown under each category label. */
export const NEAR_ME_CATEGORY_EXAMPLES: Readonly<Record<string, string[]>> =
  NEAR_ME_CATEGORY_OPTIONS.reduce<Record<string, string[]>>((acc, cat) => {
    const examples = SUBCATS_FOR_ALLOWED_CATEGORIES.filter(
      (s) => s.parentCategory === cat,
    )
      .slice(0, 3)
      .map((s) => s.name);
    acc[cat] = examples;
    return acc;
  }, {});

/**
 * True when the subscriber has no category restriction, or the item category is allowed.
 * Null/empty `nearMeCategories` means all categories (location match only).
 */
export function itemPassesNearMeCategoryFilter(
  itemCategory: string | null | undefined,
  nearMeCategories: string[] | null | undefined,
): boolean {
  const allowed = nearMeCategories?.filter((c) => c.length > 0) ?? [];
  if (allowed.length === 0) return true;
  if (!itemCategory?.trim()) return false;
  return allowed.includes(itemCategory);
}

/** Keep only values that exist on `items.category` (Postgres enum). */
export function sanitizeNearMeCategories(
  raw: string[] | null | undefined,
): string[] | null {
  if (!raw?.length) return null;
  const next = [...new Set(raw.filter((c) => ALLOWED.has(c)))].sort();
  return next.length > 0 ? next : null;
}

export function isNearMeFilterableItemCategory(value: string): boolean {
  return ALLOWED.has(value);
}

/**
 * Keep known picker-visible subcategory IDs.
 * Drops deprecated/legacy ids even if still present in saved prefs.
 */
export function sanitizeNearMeSubcategories(
  raw: string[] | null | undefined,
): string[] | null {
  if (!raw?.length) return null;
  const next = [
    ...new Set(
      raw.filter(
        (s) =>
          SUBCATEGORY_IDS.has(s) && !isHiddenFromCatalogPickerSubcategory(s),
      ),
    ),
  ].sort();
  return next.length > 0 ? next : null;
}

/**
 * Advanced subcategory filter.
 * Safe fallback: if item subcategory is missing, we keep category-only behavior.
 */
export function itemPassesNearMeSubcategoryFilter(
  itemSubcategory: string | null | undefined,
  nearMeSubcategories: string[] | null | undefined,
): boolean {
  const allowed = nearMeSubcategories?.filter((s) => s.length > 0) ?? [];
  if (allowed.length === 0) return true;
  if (!itemSubcategory?.trim()) return true;
  return allowed.includes(itemSubcategory);
}
